import * as React from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import InsightsIcon from '@mui/icons-material/Insights';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import CandlestickChartRoundedIcon from '@mui/icons-material/CandlestickChartRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { Copyright } from '../components/Copyright';
import bgImage from '../assets/signupbackground.jpg'
import { useState } from 'react';
import { LoginApi } from '../api/LoginApi';
import { KEY_TOKEN } from '../const';
import Snackbar from '@mui/material/Snackbar'
import { AxiosError } from 'axios';
import Alert from '@mui/material/Alert';
import SmartBanner from 'react-smartbanner';
import { GoogleLoginButton } from '../components/GoogleLoginButton';
import { signInWithGooglePopup } from '../api/FirebaseApi';

export const  SignUp = () => {
  const theme = useTheme()

  const [ loading, setLoading ] = useState(false)
  const [ email, setEmail ] = useState("")
  const [ password, setPassword ] = useState("")
  const [ confirmPassword, setConfirmPassword ] = useState("")
  const [ error, setError ] = useState("")
  const [ termsChecked, setTermsChecked ] = useState(false)
  const [ SSOing, setSSOing ] = useState(false)

  const signUp = async () =>
  {
    if(!termsChecked) return setError("Please accept the terms and conditions")
    try
    {
      setLoading(true)
      const loginApi = new LoginApi()
      const response = await loginApi.signup(email, password, confirmPassword)

      if(response && response.token)
      {
        localStorage.setItem(KEY_TOKEN, JSON.stringify(response))
        window.location.href = "/dashboard"
      }
    }
    catch(error)
    {
      setError(getSignupErrorResponse(error as AxiosError))
      setLoading(false)
    }
  }

  const getSignupErrorResponse = (error: AxiosError) =>
  {
    try
    {
      // @ts-ignore
      return error.message.response.data.error
    }
    catch(error)
    {
      return ""
    }
  }

  const handleGoogleLogin = async () => {
    setSSOing(true)

    try
    {
      const result = await signInWithGooglePopup() as any
      const token = await result._tokenResponse.oauthIdToken
      const photoUrl = result.user?.photoURL

      if(token)
      {
        const response = await new LoginApi().googleLogin(token, photoUrl)
        if(response?.token)
        {
          localStorage.setItem(KEY_TOKEN, JSON.stringify(response))
          window.location.href = "/dashboard"
        }
      }
    }
    catch(error)
    {
      setError("Error signing in with Google (Error 1)")
    }

    setSSOing(false)
  }

  return (
    <Box sx={styles.page(theme)}>
      <CssBaseline />
      <SmartBanner title="AlgoTrader" />

      <Grid container sx={styles.shell}>
        <Grid item xs={12} lg={7} sx={styles.heroPanel(theme, bgImage)}>
          <Box sx={styles.heroOverlay(theme)} />
          <Stack spacing={2} sx={styles.heroContent}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip icon={<BoltRoundedIcon />} label="Start trading faster" sx={styles.heroChip(theme)} />
              <Chip icon={<ShieldRoundedIcon />} label="Secure account setup" sx={styles.heroChip(theme)} />
              <Chip icon={<CandlestickChartRoundedIcon />} label="Web + mobile workflow" sx={styles.heroChip(theme)} />
            </Stack>

            <Box>
              <Typography variant="overline" sx={styles.heroEyebrow(theme)}>AlgoTrader Access</Typography>
              <Typography sx={styles.heroTitle}>
                Create your account and launch into the trading workspace.
              </Typography>
            </Box>

            <Typography sx={styles.heroCopy(theme)}>
              Sign up once to unlock the dashboard, signals, logs, subscriptions, and strategy controls across web and mobile.
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={5} sx={styles.formPanelWrap}>
          <Paper elevation={0} sx={styles.formPanel(theme)}>
            <Stack spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Box sx={styles.logo(theme)}>
                <InsightsIcon />
              </Box>
              <Typography component="h1" variant="h4" fontWeight="700">
                Create Account
              </Typography>
              <Typography variant="body2" sx={styles.formCopy(theme)}>
                Set up your AlgoTrader account to access the dashboard, subscription tools, and mobile-connected trading controls.
              </Typography>
            </Stack>

            <Box component="form" noValidate>
              <Stack spacing={1.25}>
                <TextField
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  sx={styles.field(theme)}
                />
                <TextField
                  onChange={(e) => setPassword(e.target.value)}
                  value={password}
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  sx={styles.field(theme)}
                />
                <TextField
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  value={confirmPassword}
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  id="confirmPassword"
                  sx={styles.field(theme)}
                />

                <Box sx={styles.termsRow(theme)}>
                  <FormControlLabel
                    value={termsChecked}
                    onChange={(e: any) => setTermsChecked(e.target.checked)}
                    control={<Checkbox value="remember" color="primary" />}
                    label="I accept the Terms and Conditions"
                  />
                </Box>

                <Button
                  disabled={loading}
                  onClick={signUp}
                  fullWidth
                  variant="contained"
                  sx={styles.primaryButton}
                >
                  {loading ? "Signing up..." : "Sign Up"}
                </Button>

                <Typography sx={styles.dividerCopy(theme)} textAlign="center">Or continue with</Typography>

                <GoogleLoginButton
                  disabled={SSOing}
                  onClick={handleGoogleLogin}
                  text={SSOing ? 'SSO in progress' : 'Google'} />

                <Grid container sx={{ mt: 0.5 }}>
                  <Grid item xs>
                    <Link href="/terms" variant="body2" underline="hover">
                      Terms and Conditions
                    </Link>
                  </Grid>
                  <Grid item>
                    <Link href="/privacy" variant="body2" underline="hover">
                      Privacy Policy
                    </Link>
                  </Grid>
                </Grid>

                <Copyright sx={{ mt: 2 }} />
              </Stack>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={!!error} onClose={() => setError("")} autoHideDuration={5000} >
        <Alert severity="error" icon={<></>} style={{ backgroundColor: "#EF5350", color: "white" }}>{error}</Alert>
      </Snackbar>
    </Box>
  );
}

const styles = {
  page: (theme: any) => ({
    minHeight: '100vh',
    background:
      theme.palette.mode === 'dark'
        ? `radial-gradient(circle at top left, ${alpha('#22d3ee', 0.12)}, transparent 24%), linear-gradient(180deg, #06101c 0%, #091421 100%)`
        : `radial-gradient(circle at top left, ${alpha('#60a5fa', 0.18)}, transparent 24%), linear-gradient(180deg, #f3f8ff 0%, #e8f1ff 100%)`,
  }),
  shell: {
    minHeight: '100vh',
  },
  heroPanel: (theme: any, bgImage: string) => ({
    position: 'relative',
    display: { xs: 'none', lg: 'flex' },
    alignItems: 'flex-start',
    padding: 3,
    backgroundImage: `url(${bgImage})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  }),
  heroOverlay: (theme: any) => ({
    position: 'absolute',
    inset: 0,
    background:
      theme.palette.mode === 'dark'
        ? `linear-gradient(180deg, ${alpha('#05101b', 0.55)} 0%, ${alpha('#07111c', 0.82)} 100%)`
        : `linear-gradient(180deg, ${alpha('#eaf3ff', 0.38)} 0%, ${alpha('#dceaff', 0.72)} 100%)`,
  }),
  heroContent: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 620,
    paddingTop: 8,
  },
  heroChip: (theme: any) => ({
    borderRadius: '999px',
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.1 : 0.74),
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.1 : 0.45)}`,
    '& .MuiChip-label': {
      fontWeight: 700,
    },
  }),
  heroEyebrow: (theme: any) => ({
    letterSpacing: 1.8,
    color: alpha('#ffffff', 0.72),
  }),
  heroTitle: {
    marginTop: 8,
    fontSize: 'clamp(3rem, 5vw, 5rem)',
    lineHeight: 0.94,
    maxWidth: 700,
  },
  heroCopy: (theme: any) => ({
    maxWidth: 560,
    color: alpha('#ffffff', 0.8),
    fontSize: 16,
  }),
  formPanelWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: { xs: 2, md: 3 },
  },
  formPanel: (theme: any) => ({
    width: '100%',
    maxWidth: 520,
    padding: { xs: 2, md: 2.5 },
    borderRadius: '22px',
    background:
      theme.palette.mode === 'dark'
        ? `linear-gradient(180deg, ${alpha('#0f1d2f', 0.97)} 0%, ${alpha('#0a1422', 0.98)} 100%)`
        : `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#eef5ff', 0.98)} 100%)`,
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.08 : 0.58)}`,
    boxShadow: theme.palette.mode === 'dark' ? '0 22px 60px rgba(0,0,0,0.3)' : '0 22px 52px rgba(118, 149, 191, 0.16)',
  }),
  logo: (theme: any) => ({
    width: 52,
    height: 52,
    borderRadius: '14px',
    display: 'grid',
    placeItems: 'center',
    color: theme.palette.info.main,
    backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.16 : 0.14),
  }),
  formCopy: (theme: any) => ({
    textAlign: 'center',
    color: alpha(theme.palette.text.primary, 0.68),
    maxWidth: 420,
  }),
  field: (theme: any) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.03 : 0.82),
    },
  }),
  termsRow: (theme: any) => ({
    borderRadius: '14px',
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.03 : 0.68),
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.06 : 0.4)}`,
    px: 1,
  }),
  primaryButton: {
    minHeight: 48,
    borderRadius: '14px',
    boxShadow: 'none',
  },
  dividerCopy: (theme: any) => ({
    color: alpha(theme.palette.text.primary, 0.6),
    paddingTop: 2,
  }),
}
